import { faMicroscope, faQuestionCircle, faRectanglePro } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AvatarV2 as Avatar,
  NavItem,
  NavItemHeader,
  NavItemMinimizable,
  Popover,
} from '@feathr/components';
import NotificationsPopover from '@feathr/extender/components/NotificationsPopover';
import {
  SessionCacheProvider,
  useBlackboxVersion,
  useFeatureLimits,
  useLicense,
} from '@feathr/extender/hooks';
import { useAccount, useFlags, useLocalUrl, useNavigation, useUser } from '@feathr/extender/state';

import CreditsCard from '../CreditsCard';
import EmailUsageIndicator from '../EmailUsageIndicator';
import HelpCard from '../HelpCard/HelpCard';
import HelpNav from '../HelpNav';
import UserNav from '../UserNav';

import * as styles from './NavBottomSection.css';

interface INavBottomSectionProps {
  className?: string;
  showShadow?: boolean;
}

function NavBottomSection({
  className,
  showShadow = false,
}: Readonly<INavBottomSectionProps>): JSX.Element {
  const { isMinimized } = useNavigation();
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const account = useAccount();
  const user = useUser();
  const hasProResources = useLicense('proResourcesPageVisible');
  const { blackboxVersion, isOnProduction } = useBlackboxVersion();
  const { showGrowthCredits } = useFlags();
  const { isEmailLimitsEnabled } = useFeatureLimits();

  const picture = (!!user && user.get('picture')) || '';
  const userName = user.name || user.email;
  const showHelpCard =
    account.isPlatformLite && user.getSetting('show_feathr_lite_help_card', false);
  const showDevelopmentNavUserSetting = user.getSetting('show_dev_nav') ?? false;
  const showDevNavItem =
    !!blackboxVersion &&
    !isOnProduction &&
    (account.isTestAccount || showDevelopmentNavUserSetting);

  return (
    <div
      className={classNames(styles.root, className, {
        [styles.minimized]: isMinimized,
        [styles.showShadow]: showShadow,
      })}
      data-name={'nav-bottom-section'}
    >
      {showDevNavItem && (
        <NavItemMinimizable
          data-command={'true'}
          data-command-description={t('Development and QA tools')}
          data-command-keywords={'development,qa,testing,debug'}
          data-command-label={t('Development & QA')}
          icon={faMicroscope}
          isMinimized={isMinimized}
          label={t('Development & QA')}
          to={localUrl('/development-qa')}
        />
      )}

      <NavItem
        className={styles.help}
        footer={
          <>
            {!isMinimized && showHelpCard && <HelpCard />}
            {!isMinimized && isEmailLimitsEnabled && (
              <SessionCacheProvider>
                <EmailUsageIndicator />
              </SessionCacheProvider>
            )}
            {!isMinimized && showGrowthCredits && <CreditsCard />}
            <Popover align={'end'} className={styles.popover} position={'right'} toggle={'onClick'}>
              <NavItemHeader prefix={<FontAwesomeIcon icon={faQuestionCircle} />}>
                {!isMinimized && t('Help')}
              </NavItemHeader>
              <HelpNav />
            </Popover>
          </>
        }
        tooltip={isMinimized ? t('Help') : undefined}
      />

      {hasProResources && (
        <NavItemMinimizable
          data-command={'true'}
          data-command-description={t('Access professional resources')}
          data-command-keywords={'pro,resources,professional'}
          data-command-label={t('Pro Resources')}
          icon={<FontAwesomeIcon icon={faRectanglePro} />}
          isMinimized={isMinimized}
          label={t('Resources')}
          to={localUrl('/settings/pro-resources')}
        />
      )}

      <NavItem
        className={styles.notifications}
        footer={<NotificationsPopover isMinimized={isMinimized} />}
        tooltip={isMinimized ? t('Notifications') : undefined}
      />

      <NavItem
        className={styles.avatar}
        footer={
          <Popover align={'end'} className={styles.popover} position={'right'} toggle={'onClick'}>
            <NavItemHeader prefix={<Avatar image={picture} name={userName} size={'xs'} />}>
              {!isMinimized && userName}
            </NavItemHeader>
            <UserNav />
          </Popover>
        }
        tooltip={isMinimized ? userName : undefined}
      />
    </div>
  );
}

export default NavBottomSection;
